(function () {
  // VPP: prevent facets.js from executing twice
  if (window.__vpp_facets_loaded) return;
  window.__vpp_facets_loaded = true;

/* ==========================================================
   LUMIA / VPP — facets.js (ORIGINAL BASE + SAFE CUSTOMIZATIONS)
   Goals:
   - Keep original theme behavior
   - Add price range drag fix (no reload while dragging)
   - Fix known bugs (duplicate enableLoading, FacetRemove currentTarget)
   - Prevent homepage/global JS crashes (guards + safe customElements.define)
   - Safe tippy usage (won't throw if not present)
   ========================================================== */

/* ---------- Safe debounce fallback (prevents crashes on pages missing debounce) ---------- */
// VPP: debounce helper (safe even if this file is loaded twice)
window.__vppDebounce =
  window.__vppDebounce ||
  window.debounce ||
  function (fn, wait) {
    let t;
    return function () {
      const args = arguments;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };


class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.debouncedOnSubmit = __vppDebounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const form = this.querySelector("form");
    if (!form) return; // ✅ IMPORTANT: do nothing if markup isn't present

    // ✅ Customization: Do NOT submit facets on RANGE 'input' (drag). Submit on 'change' (release).
    // This prevents sidebar re-render from cancelling slider drag.
    form.addEventListener("input", (e) => {
      const t = e.target;
      const isRange = t?.matches?.('input[type="range"]');
      const inPriceSlider = !!t?.closest?.("price-slider");
      if (isRange || inPriceSlider) return; // allow smooth dragging
      this.debouncedOnSubmit(e);
    });

    form.addEventListener("change", (e) => {
      const t = e.target;
      const isRange = t?.matches?.('input[type="range"]');
      const inPriceSlider = !!t?.closest?.("price-slider");
      if (isRange || inPriceSlider) {
        this.debouncedOnSubmit(e); // apply on release
      }
    });

    // Keep original number handling
    this.querySelectorAll('form input[type="number"]').forEach((el) =>
      el.addEventListener("focusout", this.debouncedOnSubmit.bind(this))
    );
    this.querySelectorAll('form input[type="number"]').forEach((el) =>
      el.addEventListener("keypress", this.debouncedOnSubmit.bind(this))
    );

    document.querySelector('[name=sort_by]')?.addEventListener("change", this.onSubmitHandler);

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper && typeof window.onKeyUpEscape === "function") {
      facetWrapper.addEventListener("keyup", onKeyUpEscape);
    }
  }

  static getLoader() {
    return document.querySelector("loading-bar");
  }

  static disableSidebar() {
    document.querySelector(".facets-container")?.classList.add("disable-filters");
  }

  static enableSidebar() {
    document.querySelectorAll(".facets-container").forEach((item) => item.classList.remove("disable-filters"));
  }

  static facetsLoaded() {
    return document.querySelector("#filterColumn .filter-col-content");
  }

  static async asyncLoadSidebar(url, callback) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const target = document.querySelector("#filterColumn");
        const sourceSection = html.querySelector(".shopify-section");

        if (target && sourceSection) {
          target.innerHTML = sourceSection.innerHTML;
          FacetFiltersForm.renderElements(html, false);
        }

        callback && callback();

        // ✅ Re-apply sticky after sidebar is reloaded (if you have this helper elsewhere)
        window.__vppEnsureStaticFilter?.();
      })
      .catch((err) => console.error("asyncLoadSidebar error:", err));
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static disableAllFacets() {
    document.querySelectorAll("facet-filters-form .input-wrap").forEach((element) => {
      element.classList.add("is-disable");
      element.querySelectorAll("[data-facet-count]").forEach((item) => (item.textContent = "(0)"));
    });
  }

  // ✅ Fix: original theme had enableLoading() twice (second overwrote the first)
  static enableLoading() {
    document.getElementById("ProductGridContainer")?.classList.add("loading");
  }

  static disableLoading() {
    document.getElementById("ProductGridContainer")?.classList.remove("loading");
  }

  static renderPage(searchParams, event, updateURLHash = true, postAjaxCallback) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    if (!sections.length) return; // ✅ Important: prevents homepage errors

    const gridContainer = document.getElementById("ProductGridContainer");
    const collection = gridContainer?.querySelector(".collection");
    const sorting = document.querySelector("[data-facets-sorting]");
    const countContainerDesktop = document.querySelector(".ProductCountDesktop");

    collection?.classList.add("loading");
    sorting?.classList.add("loading");

    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
      FacetFiltersForm.getLoader()?.start?.();
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, postAjaxCallback)
        : FacetFiltersForm.renderSectionFromFetch(url, event, postAjaxCallback);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, postAjaxCallback) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        if (responseText) {
          const html = responseText;
          FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderVendorsFilterOnSearchPage(html);
          FacetFiltersForm.renderElements(html);
          FacetFiltersForm.renderProductCount(html);
          FacetFiltersForm.enableSidebar();
          FacetFiltersForm.getLoader()?.hide?.();
          postAjaxCallback && postAjaxCallback();

          window.__vppEnsureStaticFilter?.();
        } else {
          document.querySelectorAll(".loading-overlay").forEach((item) => item.classList.add("hidden"));
          document.querySelectorAll(".loading").forEach((item) => item.classList.remove("loading"));
          document.querySelector(".facets-container")?.classList.remove("disable-filters");
          FacetFiltersForm.getLoader()?.hide?.();
        }
      })
      .catch((err) => console.error("renderSectionFromFetch error:", err));
  }

  static renderSectionFromCache(filterDataUrl, event, postAjaxCallback) {
    const cached = FacetFiltersForm.filterData.find(filterDataUrl);
    if (!cached) return;

    const html = cached.html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderVendorsFilterOnSearchPage(html);
    FacetFiltersForm.renderElements(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.getLoader()?.hide?.();
    postAjaxCallback && postAjaxCallback();

    window.__vppEnsureStaticFilter?.();
  }

  static renderVendorsFilterOnSearchPage(html) {
    const renderedDocument = new DOMParser().parseFromString(html, "text/html");
    const vendorsFilter = document.querySelector("body.template-search .category-page-description");
    if (vendorsFilter) {
      const source = renderedDocument.querySelector(".category-page-description");
      source && (vendorsFilter.innerHTML = source.innerHTML);
    }
  }

  static renderElements(html, parse = true) {
    let renderedDocument = new DOMParser().parseFromString(html, "text/html");
    if (!parse) renderedDocument = html;

    document.querySelectorAll("[data-render-facets]").forEach((destination) => {
      const source = renderedDocument.querySelector(`[data-render-facets=${destination.dataset.renderFacets}]`);
      if (source) destination.innerHTML = source.innerHTML;
    });

    FacetFiltersForm.enableSidebar();
    window.__vppEnsureStaticFilter?.();
  }

  static renderProductGridContainer(html) {
    const renderedDocument = new DOMParser().parseFromString(html, "text/html");

    const dest = document.getElementById("ProductGridContainer");
    const src = renderedDocument.getElementById("ProductGridContainer");
    if (dest && src) dest.innerHTML = src.innerHTML;

    const filterToggleEl = document.querySelector("save-attr[data-filter-toggle]");
    const filterToggleElOld = renderedDocument.querySelector("save-attr[data-filter-toggle]");
    if (filterToggleEl && filterToggleElOld) {
      filterToggleEl.parentNode.replaceChild(filterToggleElOld, filterToggleEl);
    }

    document.querySelector('[name=sort_by]')?.addEventListener("change", function (event) {
      event.preventDefault();
      const formData = new FormData(event.target.form ? event.target.form : event.target.closest("form"));
      const searchParams = new URLSearchParams(formData).toString();
      FacetFiltersForm.disableSidebar();
      FacetFiltersForm.renderPage(searchParams, event);
      event.stopImmediatePropagation();
    });
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, "text/html");
    let countElement = parsed.querySelector(".ProductCountDesktop");
    const facetsSorting = document.querySelector("[data-facets-sorting]");

    if (countElement) {
      const count = countElement.innerHTML;
      const countText = countElement.innerText;
      const containerDesktop = document.querySelectorAll(".ProductCountDesktop");

      containerDesktop?.forEach((value) => {
        value.innerHTML = count;
        value.classList.remove("loading");
      });

      const match = countText.match(/\d+/);
      const n = match ? parseInt(match[0], 10) : 0;

      if (!n) facetsSorting?.classList.add("hidden");
      else facetsSorting?.classList.remove("hidden");
    } else {
      facetsSorting?.classList.add("hidden");
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const facetDetailsElements = parsedHTML.querySelectorAll("#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter");

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };

    const countsToRender = Array.from(facetDetailsElements).find(matchesIndex);

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    if (countsToRender && event?.target?.closest?.(".js-filter")) {
      FacetFiltersForm.renderCounts(countsToRender, event.target.closest(".js-filter"));
    }

    if (parsedHTML.querySelector(".category-empty")) {
      FacetFiltersForm.disableAllFacets();
    }

    const filterPopupElement = document.querySelector("filter-popup");
    filterPopupElement && typeof filterPopupElement.render === "function" && filterPopupElement.render();

    document.querySelector(".active-facets-desktop")?.dispatchEvent(new Event("filter:updated"));
  }

  static renderActiveFacets(html) {
    const selectors = [".active-facets-mobile", ".active-facets-desktop"];
    selectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelectorAll(selector).forEach((item) => (item.innerHTML = activeFacetsElement.innerHTML));
    });
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [".mobile-facets__open", ".mobile-facets__count", ".sorting"];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const dest = document.querySelector(selector);
      const src = html.querySelector(selector);
      if (dest && src) dest.innerHTML = src.innerHTML;
    });

    const isMobile = window.matchMedia("(max-width:991px)").matches;
    if (!isMobile) {
      // Only scroll if helpers exist (prevents crashes)
      if (typeof window.getCoords !== "function" || typeof window.smoothScrollTo !== "function") return;

      let scrollOffset;
      const scrollCollectionElement = document.querySelector("#ProductGridContainer");
      const vendorsFilter = document.querySelector("body.template-search .category-page-description");

      if (vendorsFilter) scrollOffset = getCoords(vendorsFilter);
      else if (scrollCollectionElement) scrollOffset = getCoords(scrollCollectionElement);

      scrollOffset && smoothScrollTo(scrollOffset.top - 20, 700);
    }
  }

  static renderCounts(source, target) {
    const targetElement = target?.querySelector(".facets__selected");
    const sourceElement = source?.querySelector(".facets__selected");
    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, "", `${window.location.pathname}${searchParams && "?".concat(searchParams)}`);
  }

  static getSections() {
    const pg = document.getElementById("product-grid");
    const id = pg?.dataset?.id;
    if (!id) return []; // ✅ prevents homepage crash
    return [{ section: id }];
  }

  onSubmitHandler(event) {
    if (!event || !event.target) return;
    if (event.target.classList.contains("search")) return;

    // ✅ Safety: do NOT renderPage on range drag input
    const isRange = event.target?.matches?.('input[type="range"]');
    const inPriceSlider = !!event.target?.closest?.("price-slider");
    if ((isRange || inPriceSlider) && event.type === "input") return;

    let value = parseInt(event.target.dataset.value, 10);

    if (event.target.type === "number" && event.type === "keypress" && event.key === "Enter") {
      event.target.addEventListener(
        "focusout",
        function (e) {
          e.stopPropagation();
        },
        true
      );
    }

    if (
      (event.target.type === "number" && event.type === "focusout") ||
      (event.target.type !== "number" && (event.type === "input" || event.type === "change")) ||
      (event.target.type === "number" && event.type === "keypress" && event.key === "Enter")
    ) {
      event.preventDefault();

      // If dataset.value isn't present, don't block updates
      const currentVal = parseInt(event.target.value, 10);
      const shouldRun = isNaN(value) ? true : value !== currentVal;

      if (shouldRun) {
        const form = event.target.form ? event.target.form : event.target.closest("form");
        if (!form) return;
        const formData = new FormData(form);
        const searchParams = new URLSearchParams(formData).toString();
        FacetFiltersForm.disableSidebar();
        FacetFiltersForm.renderPage(searchParams, event);
        event.stopImmediatePropagation();
      }
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);

// ✅ IMPORTANT: guard custom element defines (prevents DOMException that can break homepage)
if (window.customElements && !customElements.get("facet-filters-form")) {
  if (!customElements.get('facet-filters-form')) {
  customElements.define('facet-filters-form', FacetFiltersForm);
}

}
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input").forEach((element) =>
      element.addEventListener("change", this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;

    if (maxInput.value) minInput.setAttribute("max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "") minInput.setAttribute("max", minInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

if (window.customElements && !customElements.get("price-range")) {
  customElements.define("price-range", PriceRange);
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();

    const ajax = this.dataset.ajax;

    this.querySelectorAll("a").forEach((item) => {
      item.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        // ✅ Fix: use e.currentTarget (original code used global "event")
        const currentTarget = e.currentTarget;

        if (currentTarget) {
          if (
            !FacetFiltersForm.facetsLoaded() ||
            ("filterLoadForce" in this.dataset && this.dataset.filterLoadForce === "true")
          ) {
            FacetFiltersForm.asyncLoadSidebar(ajax, () => {
              this.onActiveFilterClick(e, currentTarget);
            });
          } else {
            FacetFiltersForm.disableSidebar();
            this.onActiveFilterClick(e, currentTarget);
          }
        }
      });
    });
  }

  onActiveFilterClick(e, target) {
    e.preventDefault();
    FacetFiltersForm.toggleActiveFacets();

    if (target) {
      const url = target.href.indexOf("?") === -1 ? "" : target.href.slice(target.href.indexOf("?") + 1);
      FacetFiltersForm.renderPage(url);
    }
  }
}

if (window.customElements && !customElements.get("facet-remove")) {
  customElements.define("facet-remove", FacetRemove);
}

class FacetSortBy extends HTMLElement {
  constructor() {
    super();
    this.querySelector("select")?.addEventListener("change", this.onChange.bind(this));
  }

  onChange(e) {
    const ajax = this.dataset.ajax;
    const currentTarget = e.currentTarget;

    if (!FacetFiltersForm.facetsLoaded()) {
      FacetFiltersForm.asyncLoadSidebar(ajax, () => {
        currentTarget && currentTarget.dispatchEvent(new Event("change", { bubbles: false }));
      });
    }
  }
}

if (window.customElements && !customElements.get("facet-sort-by")) {
  customElements.define("facet-sort-by", FacetSortBy);
}

class FacetFilter extends HTMLElement {
  constructor() {
    super();
    const a = this.querySelector("a");
    if (!a) return;

    a.addEventListener("click", (event) => {
      event.preventDefault();

      const currentTarget = event.currentTarget;
      const resetUrl = currentTarget.dataset.resetUrl || "";
      const ajax = this.dataset.ajax || "";

      const filterUrlToRemove =
        resetUrl.indexOf("?") === -1
          ? ""
          : resetUrl.slice(resetUrl.indexOf("?") + 1) + "&filter.p.vendor=" + (currentTarget.dataset.value || "");

      // ✅ Safe tippy check (won't throw if tippy isn't loaded)
      if (typeof window.tippy !== "undefined") {
        window.tippy.hideAll({ exclude: this.querySelector("a")?._tippy });
      }

      this.querySelector(".loading-overlay")?.classList.toggle("hidden");

      if (!FacetFiltersForm.facetsLoaded()) {
        FacetFiltersForm.asyncLoadSidebar(ajax, () => {
          this.render(filterUrlToRemove, event, currentTarget);
        });
      } else {
        FacetFiltersForm.disableSidebar();
        this.render(filterUrlToRemove, event, currentTarget);
      }
    });

    this.addEventListener("mouseenter", () => {
      const link = this.querySelector("a");
      if (!link) return;
      if (!link.classList.contains("active")) {
        if (typeof window.tippy !== "undefined") {
          window.tippy.hideAll({ exclude: link._tippy });
        }
      }
    });
  }

  render(filterUrlToRemove, event, currentTarget) {
    if (filterUrlToRemove) {
      FacetFiltersForm.renderPage(filterUrlToRemove, event, true, () => {
        this.querySelector(".loading-overlay")?.classList.add("hidden");

        const filterToggleElement = document.querySelector("filter-toggle");
        const filterPopupElement = document.querySelector("filter-popup");

        if (filterToggleElement && typeof filterToggleElement.isFilterOpened === "function" && !filterToggleElement.isFilterOpened()) {
          filterToggleElement.dataset.filterLoadForce = true;
        } else if (filterPopupElement) {
          filterPopupElement.dataset.filterLoadForce = true;
        }
      });
    } else {
      const control = currentTarget?.dataset?.control;
      control && document.querySelector("#" + control)?.click();
    }
  }
}

if (window.customElements && !customElements.get("facet-filter")) {
  customElements.define("facet-filter", FacetFilter);
}

/* ==========================================================
   Optional: Make sure <filter-popup> has a render() method
   (prevents errors if theme expects it)
   ========================================================== */
(function () {
  function ensureFilterPopupRender() {
    const el = document.querySelector("filter-popup");
    if (!el) return;
    if (typeof el.render !== "function") {
      el.render = function () {};
    }
  }
  document.addEventListener("DOMContentLoaded", ensureFilterPopupRender);
  document.addEventListener("shopify:section:load", ensureFilterPopupRender);
})();
(() => {
  // VPP: prevent double execution of facets.js (fixes "already been declared" errors)
  if (window.__vpp_facets_loaded) return;
  window.__vpp_facets_loaded = true;
})();
})();
